import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogIn, ArrowLeft, ShieldCheck, Store, Info, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth";
import { api, errMsg } from "@/api";
import { Modal } from "@/components/Dialogs";

function ForgotSuperadmin({ open, onClose }) {
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-superadmin", { code, new_password: pw });
      toast.success(`Password admin utama (${data.username}) berhasil direset. Silakan login.`);
      onClose();
    } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Reset Password Admin Utama" testId="forgot-dialog">
      <form onSubmit={go}>
        <p className="dialog-text">Masukkan <strong>kode rahasia</strong> yang tersimpan di server (file <code>backend/.env</code> → <code>ADMIN_RESET_CODE</code>). Kode ini hanya diketahui pemilik server.</p>
        <label className="field-label">Kode Rahasia</label>
        <input className="text-input" value={code} onChange={(e) => setCode(e.target.value)} autoFocus data-testid="forgot-code" />
        <label className="field-label">Password Baru (min. 6 karakter)</label>
        <input className="text-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="forgot-password" />
        {err && <p className="error-text" data-testid="forgot-error">{err}</p>}
        <div className="actions-right">
          <button type="button" className="btn-ghost" onClick={onClose}>Batal</button>
          <button type="submit" className="btn-primary" disabled={busy || !code || pw.length < 6} data-testid="forgot-submit">{busy ? <Loader2 className="spin" size={16} /> : <KeyRound size={16} />} Reset Password</button>
        </div>
      </form>
    </Modal>
  );
}

export default function LoginPage({ title, subtitle, backTo, backLabel, variant = "store", slug }) {
  const { login } = useAuth();
  const [forgot, setForgot] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try { await login(username, password); }
    catch (err) { setError(errMsg(err, "Login gagal")); }
    finally { setBusy(false); }
  };

  return (
    <div className={`app-shell center-note ${variant === "super" ? "super-shell" : ""}`}>
      <form className="login-card" onSubmit={submit} data-testid="login-form">
        {backTo && <Link to={backTo} className="back-link" data-testid="login-back"><ArrowLeft size={14} /> {backLabel || "Kembali"}</Link>}
        <div className="login-icon">{variant === "super" ? <ShieldCheck size={26} /> : <Store size={26} />}</div>
        <h1 className="app-title">{title}</h1>
        <p className="app-subtitle">{subtitle}</p>
        <label className="field-label">Username</label>
        <input className="text-input" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" data-testid="login-username" />
        <label className="field-label">Password</label>
        <input className="text-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" data-testid="login-password" />
        {error && <p className="error-text" data-testid="login-error">{error}</p>}
        <button className="btn-primary full" type="submit" disabled={busy || !username || !password} data-testid="login-submit">
          {busy ? <Loader2 className="spin" size={16} /> : <LogIn size={16} />} Masuk
        </button>

        <div className="login-hint" data-testid="login-hint">
          <Info size={14} />
          {variant === "super" ? (
            <div>
              <strong>Halaman ini khusus Admin Utama (superadmin).</strong> Admin toko masuk lewat <code>/toko/&#123;nama-toko&#125;/admin</code>, reseller masuk dengan PIN di <code>/toko/&#123;nama-toko&#125;</code>.
              <div><button type="button" className="link-inline" onClick={() => setForgot(true)} data-testid="forgot-open">Lupa password admin utama?</button></div>
            </div>
          ) : (
            <div>
              <strong>Halaman ini khusus Admin Toko <code>{slug}</code>.</strong> Reseller masuk dengan PIN di <Link to={`/toko/${slug}`}>halaman toko</Link>; Admin Utama masuk di <Link to="/superadmin">/superadmin</Link>.
              <div className="muted-sm">Lupa password? Minta Admin Utama mereset lewat menu Admin &amp; Toko → Password.</div>
            </div>
          )}
        </div>
      </form>
      {variant === "super" && <ForgotSuperadmin open={forgot} onClose={() => setForgot(false)} />}
    </div>
  );
}
